<template>
	<app-page>
		<app-tabs app :tabs="tabs">
			<template v-slot:tab-item="{ tab }">
				<app-tab-item :tab="tab" value="strings">
					<app-heading :title="`${parent.name} Strings`">
						<template v-slot:left>
							<app-button v-if="$ravello.can('website.post')"
								class="d-none d-sm-inline-flex"
								color="success"
								small
								outlined
								:to="{ name: stringRoute, params: { string: 'new' } }"
							>Add String</app-button>
						</template>
						<template v-slot:right>
							<app-button
								v-if="$ravello.can('website.post')"
								color="secondary"
								icon="fa-file-download"
								small
								outlined
								@click="onExport"
							>
									Export
							</app-button>
							<input-file
								type="json"
								button
								small
								outlined
								label="Import"
								@load="onImport"
							/>
							<app-button v-if="$ravello.can('website.post') && !isTemplate"
								color="secondary"
								small
								outlined
								@click="onPublish"
							>Publish</app-button>
							<app-button v-if="$ravello.can('website.post') && isTemplate"
								color="primary"
								small
								outlined
								@click="onTemplatePublish"
							>Publish to Websites</app-button>
						</template>
					</app-heading>
					<app-filter :value="hasFilters" @click="applyFilters()" class="pb-4">
						<app-row no-padding>
							<app-cell cols="6" sm="4">
								<input-select
									v-model="filters.namespace"
									label="Namespace"
									:items="namespaces"
									clearable
									dense
								></input-select>
							</app-cell>
							<app-cell cols="6" sm="4">
								<input-select
									v-model="filters.category"
									label="Category"
									:items="categories"
									clearable
									dense
								></input-select>
							</app-cell>
						</app-row>
					</app-filter>
					<app-row>
						<app-cell>
							<app-data-table
								:items="strings"
								:headers="headers"
								:total="count"
								:refresh.sync="refresh"
								:initial-state="tableState"
								search
								@update="fetchStrings"
								@search="onSearch"
							>
								<template v-slot:code="{ item }">
									<app-button
										flat
										small
										class="ma-0 text-lowercase"
										icon="far fa-copy"
										icon-right
										@click="onFullSlugClick(getFullSlug(item))"
									>{{ getFullSlug(item) }}</app-button>
								</template>
								<template v-slot:actions="{ item }">
									<app-button
										v-if="$ravello.can('website.put')"
										color="secondary"
										:to="{ name: stringRoute, params: { string: item._id } }"
										icon="fa-pencil-alt"
										table
									>
										Edit
									</app-button>
									<app-button-confirm
										v-if="$ravello.can('website.delete')"
										color="error"
										icon="fa-trash"
										table
										label-confirm="Remove"
										:description="`Are you sure you want to remove string <b>${item.slug}</b>?`"
										title="Remove String"
										@click="onDelete(item._id)"
									>
										Remove
									</app-button-confirm>
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
					<app-floating-button
						class="d-inline-flex d-sm-none"
						:to="{ name: stringRoute, params: { string: 'new' } }"
						color="success"
						icon="fa-plus"
					/>
				</app-tab-item>
			</template>
		</app-tabs>

		<!-- Template Publishing Progress Dialog -->
		<fragment-template-publish-progress-dialog
			v-model="templatePublishDialog.show"
			:template-id="parentId"
			@complete="onTemplatePublishComplete"
		/>
	</app-page>
</template>

<script>
import { filters, search, tableState, websiteItemParent } from '@/mixins'
import { websiteContentTabs, websiteTemplateContentTabs } from '@/datasources/tabs'
import { importCollection, exportCollection } from '@/utils/import'
import FragmentTemplatePublishProgressDialog from '@/components/fragments/FragmentTemplatePublishProgressDialog.vue'

export default {
	components: { FragmentTemplatePublishProgressDialog },
	mixins: [filters, search, tableState, websiteItemParent],
	data() {
		return {
			parent: {
				name: '',
				slug: '',
				namespaces: '',
			},
			namespaces: [],
			categories: [],
			strings: [],
			isPublishing: false,
			tabs: [],
			count: -1,
			refresh: false,
			headers: [{
				text: 'Code',
				value: 'code',
				sortable: false,
			}, {
				text: 'Namespace',
				value: 'namespace.name',
				sortable: false,
			}, {
				text: 'Category',
				value: 'category.name',
				sortable: false,
			}, {
				text: 'Slug',
				value: 'slug',
				sortable: true,
			}, {
				text: 'Text',
				value: 'text.en',
				sortable: true,
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				width: '20%',
				sortable: false,
			}],
			filters: {
				namespace: '',
				category: '',
			},
			templatePublishDialog: {
				show: false,
			},
		}
	},
	computed: {
		stringRoute() {
			return this.isTemplate ? 'website-template-string' : 'website-string'
		},
	},
	async mounted() {
		const request = this.$ravello.graphql(this.parentNode, this.parentId)
			.fields(['name', 'slug'])
			.include(this.$ravello.graphql('namespaces').sort('name').datasource())
			.union(this.$ravello.graphql('stringCategories').sort('name').datasource())

		this.parent = await request.query()
		this.namespaces = this.parent.namespaces
		this.categories = request.results.stringCategories

		this.tabs = this.isTemplate
			? websiteTemplateContentTabs(this.parentId)
			: websiteContentTabs(this.parentId)
	},
	methods: {
		getFullSlug(item) {
			if (!item.namespace) {
				return 'Missing namespace'
			}
			if (!item.category) {
				return 'Missing category'
			}

			const { slug: namespaceSlug } = item.namespace
			const { slug: categorySlug } = item.category

			return `${namespaceSlug}.${categorySlug}.${item.slug}`
		},

		// @todo - Have a way to reduce repetition
		async fetchStrings(pagination) {
			this.updateTableState(pagination)

			const request = this.$ravello.graphql('strings')
				.parent(this.$ravello.graphql(this.parentNode, this.parentId))
				.fields(['_id', 'text', 'slug'])
				.include(this.$ravello.graphql('namespace').fields(['slug', 'name']))
				.include(this.$ravello.graphql('category').fields(['slug', 'name']))
				.fromPagination(pagination)

			if (this.appliedFilters.namespace) {
				request.filter('namespace', this.appliedFilters.namespace)
			}
			if (this.appliedFilters.category) {
				request.filter('category', this.appliedFilters.category)
			}
			if (this.count === -1 && !this.searchString) {
				request.count('count')
			}

			if (this.searchText) {
				request.search(this.searchText)
			}
			try {
				this.strings = await request.query()
				if (this.searchText) {
					this.count = this.strings.length
				}
				if (this.count === -1) {
					this.count = request.results.count
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		async onDelete(id) {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to(this.parentEndpoint)
					.to(this.parentId)
					.to('strings')
					.to(id)
					.delete()
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		async onFullSlugClick(slug) {
			try {
				await this.$copyText(slug)
				this.$notifications.pushSnackbar('String code copied to clipboard!')
			} catch (err) {
				this.$notifications.pushAlert('error', err.message)
			}
		},

		async onPublish() {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to(this.parentEndpoint)
					.to(this.parentId)
					.to('strings')
					.to('build')
					.create()
				this.$notifications.pushAlert('success', 'Strings published!')
			} catch (err) {
				this.$notifications.pushAlert('error', err.data ? err.data.message : err.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		importRequest() {
			return this.$ravello.request()
				.v(2)
				.to(this.parentEndpoint)
				.to(this.parentId)
				.to('strings')
		},

		importNamespacesRequest() {
			return this.$ravello.request()
				.v(2)
				.to(this.parentEndpoint)
				.to(this.parentId)
				.to('namespaces')
		},

		importCategoriesRequest() {
			return this.$ravello.request()
				.v(2)
				.to('stringcategories')
		},

		getReplacements() {
			return [{
				request: this.importNamespacesRequest(),
				findKey: 'namespace',
				replaceKey: 'slug',
			}, {
				request: this.importCategoriesRequest(),
				findKey: 'category',
				replaceKey: 'slug',
			}]
		},

		async onExport() {
			this.$store.dispatch('app/startLoading')

			const request = this.importRequest().fields(['slug', 'namespace', 'category', 'text'])
			await exportCollection(`${this.website.slug}_strings.json`, request, {
				count: this.count,
				replacements: this.getReplacements(),
			})

			this.$store.dispatch('app/stopLoading')
		},

		async onImport(importData) {
			this.$store.dispatch('app/startLoading')

			await importCollection(importData, this.importRequest(), ['category', 'namespace', 'slug'], {
				replacements: this.getReplacements(),
			})
			this.refresh = 'hard'

			this.$store.dispatch('app/stopLoading')
		},

		onTemplatePublish() {
			this.templatePublishDialog.show = true
		},

		onTemplatePublishComplete(result) {
			const { published, errors } = result

			if (errors === 0) {
				this.$notifications.pushAlert(
					'success',
					`Successfully published template content to ${published} website${published !== 1 ? 's' : ''}!`,
				)
			} else if (published > 0) {
				this.$notifications.pushAlert(
					'warning',
					`Published to ${published} website${published !== 1 ? 's' : ''} with ${errors} error${errors !== 1 ? 's' : ''}. ` +
					'Check the details above.',
				)
			} else {
				this.$notifications.pushAlert(
					'error',
					'Failed to publish to any websites. Please check the error details.',
				)
			}
		},
	},
}
</script>
